import axios, { AxiosError, type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios';

import { clearSession } from '@/lib/auth-session';
import { useAuthStore } from '@/store/authStore';

export const api = axios.create({
  baseURL: '/api/v1',
});

api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

type RetriedRequest = InternalAxiosRequestConfig & { _retry?: boolean };

let refreshing: Promise<string> | null = null;

async function performRefresh(): Promise<string> {
  const refreshToken = useAuthStore.getState().refreshToken;
  if (!refreshToken) {
    throw new Error('NO_REFRESH_TOKEN');
  }
  const res = await axios.post<{ accessToken: string }>('/api/v1/auth/refresh', {
    refreshToken,
  });
  useAuthStore.getState().setAccessToken(res.data.accessToken);
  return res.data.accessToken;
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetriedRequest | undefined;
    const status = error.response?.status;

    const isRefreshCall = original?.url?.includes('/auth/refresh');
    if (status !== 401 || !original || original._retry || isRefreshCall) {
      return Promise.reject(error);
    }

    original._retry = true;
    try {
      const tokenPromise = refreshing ?? (refreshing = performRefresh());
      const newToken = await tokenPromise;
      refreshing = null;
      original.headers.Authorization = `Bearer ${newToken}`;
      return api.request(original as AxiosRequestConfig);
    } catch (refreshErr) {
      refreshing = null;
      clearSession();
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
      return Promise.reject(refreshErr);
    }
  },
);
